import React, { useState, useEffect, useRef } from "react";
import "./NavBar.css";
import "animate.css";
import { searchTracks } from "../api/spotifyapi";
import SearchPopup from "./searchPopup";
import AuthPopup from "./AuthPopup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function NavBar({
  onTrackSelect,
  setCurrentPage,
  setUsernamePopup,
  usernamePopup,
  isLoggedIn,
  setIsLoggedIn,
  setUsername,
  username,
}) {
  const [showPopup, setShowPopup] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [searchResult, setSearchResult] = useState([]);
  const [animationClass, setAnimationClass] = useState("animate__fadeInUp");
  const popupContentRef = useRef(null);

  const handleFocus = () => setShowPopup(true);

  const handleClosePopup = () => {
    setAnimationClass("animate__fadeOutDown");
    setTimeout(() => {
      setShowPopup(false);
      setAnimationClass("animate__fadeInUp");
    }, 300);
  };

  useEffect(() => {
    if (!searchVal.trim()) return setSearchResult([]);
    const fetchTracks = async () => {
      try {
        const results = await searchTracks(searchVal);
        setSearchResult(results);
      } catch {
        setSearchResult([]);
      }
    };
    const searchDebounce = setTimeout(fetchTracks, 500);
    return () => clearTimeout(searchDebounce);
  }, [searchVal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popupContentRef.current &&
        !popupContentRef.current.contains(event.target) &&
        !event.target.closest("#query")
      ) {
        handleClosePopup();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Hotkey support for search focusing (Ctrl/Cmd + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        document.getElementById("query")?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleTrackSelection = (track) => {
    setShowPopup(false);
    setSearchVal("");
    onTrackSelect(track);
  };

  const handleLogin = async (formData) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/auth/login`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("token", data.token);
        setUsername(data.username);
        setIsLoggedIn(true);
        setUsernamePopup(false);
        toast.success("Login Successful");
      } else {
        toast.error(data.message || "Login failed");
      }
    } catch (error) {
      toast.error(error.message || "Login Error");
    }
  };

  const handleRegister = async (formData) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/auth/register`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );
      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("token", data.token);
        setUsername(data.username);
        setIsLoggedIn(true);
        setUsernamePopup(false);
        toast.success("Registration Successful");
      } else {
        toast.error(data.message || "Registration failed");
      }
    } catch (error) {
      toast.error(error.message || "Registration failed");
    }
  };

  return (
    <>
      <nav className="navbar-container">
        <div className="navbar-main">
          {/* Logo Brand */}
          <div className="navbar-logo" onClick={() => setCurrentPage("Home")}>
            <div className="logo-icon-wrapper">
              <svg viewBox="0 0 24 24" fill="none" className="logo-svg-icon">
                <defs>
                  <linearGradient id="logo-grad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#1db954" />
                    <stop offset="100%" stopColor="#00f2fe" />
                  </linearGradient>
                </defs>
                <path
                  d="M3 10v4M6 6v12M9 10v4M12 3v18M15 8v8M18 5v14M21 10v4"
                  stroke="url(#logo-grad)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="logo-glow-dot"></span>
            </div>
            <span className="logo-text">Resonance</span>
          </div>

          {/* Search Box */}
          <div className="navbar-search-wrapper">
            <div className="search-input-container">
              <svg className="search-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                id="query"
                className="navbar-search-input"
                type="search"
                placeholder="Search tracks..."
                onFocus={handleFocus}
                autoComplete="off"
                onChange={(e) => setSearchVal(e.target.value)}
                value={searchVal}
                name="searchbar"
              />
              <div className="search-shortcut">
                <kbd>⌘</kbd>
                <kbd>K</kbd>
              </div>
            </div>
          </div>

          {/* Menu Actions */}
          <div className="navbar-actions">
            <button
              className="nav-btn"
              onClick={(e) => {
                e.preventDefault();
                setCurrentPage("Home");
              }}
            >
              Home
              <span className="nav-btn-line"></span>
            </button>
            {isLoggedIn ? (
              <div className="user-profile-badge">
                <div className="avatar-circle">
                  {username ? username[0].toUpperCase() : "U"}
                </div>
                <span className="welcome-text">
                  Hi, <strong>{username}</strong>
                </span>
                <button
                  className="logout-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    localStorage.removeItem("token");
                    setIsLoggedIn(false);
                    setUsername("");
                  }}
                  title="Logout"
                >
                  <svg className="logout-icon-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
                  </svg>
                  Log Out
                </button>
              </div>
            ) : (
              <button
                className="login-btn"
                onClick={(e) => {
                  e.preventDefault();
                  setUsernamePopup(true);
                }}
              >
                <span>Log In</span>
                <svg className="login-arrow-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                  <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
              </button>
            )}
          </div>
        </div>
      </nav>

      {showPopup && (
        <SearchPopup
          animationClass={animationClass}
          popupContentRef={popupContentRef}
          searchResult={searchResult}
          onTrackSelect={handleTrackSelection}
        />
      )}

      {usernamePopup && (
        <AuthPopup
          animationClass={animationClass}
          setUsernamePopup={setUsernamePopup}
          onLogin={handleLogin}
          onRegister={handleRegister}
        />
      )}
      <ToastContainer position="top-right" autoClose={3000} toastStyle={{ backgroundColor: '#000', color: '#fff' }} />
    </>
  );
}

export default NavBar;
